import apiClient from './client';

export interface Assessment {
  id: number;
  name: string;
  date: string;
  location?: string | null;
  status?: string | null;
  isDoubleAssessment?: boolean;
}

export interface AssessmentParticipant {
  id: number;
  participantType: string;
  status: string;
  teamKey?: 'A' | 'B';
  portalCandidate?: {
    id: number;
    name: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    maritalStatus?: string | null;
    wifeFirstName?: string | null;
    wifeLastName?: string | null;
    wifeEmail?: string | null;
    wifePhone?: string | null;
    spouseName?: string | null;
    headshotImagePath?: string | null;
    familyPhotoImagePath?: string | null;
    targetLocationMississippi?: string | null;
    onboardingStep?: number;
    onboardingCompleted?: boolean;
    profileCompleted?: boolean;
    theologyCompleted?: boolean;
    referencesCompleted?: boolean;
    sendingChurchCompleted?: boolean;
    prepareEnrichRequested?: boolean;
    prepareEnrichData?: any;
    prepareEnrichStatus?: 'not_started' | 'started' | 'scheduled' | 'complete';
    spiritualHealthCompleted?: boolean;
    spiritualHealthCandidateSubmitted?: boolean;
    spiritualHealthSpouseSubmitted?: boolean;
    backgroundCheckStatus?: 'not_started' | 'started' | 'submitted' | 'received';
    sermonReferenceUrl?: string | null;
    portalProgressPercentage?: number;
    teamProgressPercentage?: number;
    portalStatus?: string;
    sendingChurch?: {
      churchName?: string | null;
      primaryContactName?: string | null;
      primaryContactPhone?: string | null;
      noSendingChurch?: boolean;
      noSendingChurchExplanation?: string | null;
    } | null;
    user?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

export interface AssessmentDetail extends Assessment {
  description?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  status?: string | null;
  participants?: AssessmentParticipant[];
  interviewAreas?: Array<{
    id: number;
    assessmentId: number;
    name: string;
    sortOrder: number;
    assessorUserId?: number | null;
    assessorName?: string | null;
    assessorUser?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
  }>;
  eventRoles?: Array<{
    id: number;
    assessmentId: number;
    roleId: number;
    assignedUserId?: number | null;
    assignedName?: string | null;
    assistantUserId?: number | null;
    assistantName?: string | null;
    role?: {
      id: number;
      name: string;
      displayName: string;
      roleType: 'assessor' | 'support';
      isInterviewArea: boolean;
      interviewAudience?: 'couple' | 'candidate_only' | 'spouse_only';
      sortOrder: number;
    };
    assignedUser?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
    assistantUser?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    } | null;
    teamAssignments?: Array<{
      id: number;
      teamKey: 'A' | 'B';
      assignedUserId?: number | null;
      assignedName?: string | null;
      assistantUserId?: number | null;
      assistantName?: string | null;
      assignedUser?: { id: number; firstName: string; lastName: string; email: string } | null;
      assistantUser?: { id: number; firstName: string; lastName: string; email: string } | null;
    }>;
  }>;
  assignments?: Array<{
    id: number;
    assessmentId: number;
    participantId: number;
    roleId: number;
    assessorId: number;
  }>;
}

export interface AssessmentAttendanceEntry {
  id: number;
  assessmentId: number;
  userId?: number | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  isAttending: boolean;
  hotelNeeded: boolean;
  hotelSundayNight?: boolean;
  hotelMondayNight?: boolean;
  guestCount: number;
  guestNames: string[];
  dietaryRestrictions?: string | null;
  createdAt?: string;
  updatedAt?: string;
  resolvedName?: string;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  } | null;
}

export interface AssessmentEventDetails {
  assessment: Assessment;
  totals: {
    candidates: number;
    spouses: number;
    assignedRoles: number;
    attendanceAdditional: number;
    totalAttendees: number;
    attendingConfirmations: number;
    hotelNeeded: number;
    guestCount: number;
  };
  attendance: {
    entries: AssessmentAttendanceEntry[];
    dietary: Array<{ name: string; details: string }>;
    guests: Array<{ name: string; guestCount: number; guestNames: string[] }>;
  };
  assignedRoles: {
    users: Array<{
      id: number;
      firstName: string;
      lastName: string;
      email: string;
      phone?: string | null;
      roleName?: string;
    }>;
    names: Array<{ name: string }>;
  };
}

export interface AttendancePayload {
  isAttending?: boolean;
  hotelNeeded?: boolean;
  hotelSundayNight?: boolean;
  hotelMondayNight?: boolean;
  guestCount?: number;
  guestNames?: string[] | string;
  dietaryRestrictions?: string | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  userId?: number | null;
}

export interface AssessmentInterviewMatrixCell {
  id: number;
  assessmentId: number;
  interviewPeriodIndex: number;
  roleId: number;
  participantId?: number | null;
  participantPerson?: 'couple' | 'candidate' | 'spouse' | null;
  teamKey?: 'A' | 'B';
  participant?: AssessmentParticipant;
}

export interface InterviewMatrixResponse {
  roles: Array<{
    id: number;
    name: string;
    displayName: string;
    roleType: 'assessor' | 'support';
    isInterviewArea: boolean;
    interviewAudience?: 'couple' | 'candidate_only' | 'spouse_only';
    sortOrder: number;
  }>;
  participants: AssessmentParticipant[];
  cells: AssessmentInterviewMatrixCell[];
  periods: number[];
  teams?: Array<'A' | 'B'>;
}

/** Provenance recorded when an assessor accepts an AI suggestion. */
export interface AiAssistMetadata {
  model?: string;
  draftedAt?: string;
  rewrittenAt?: string;
}

export interface AiDraftResult {
  candidateFacing: string;
  internalNotes: string;
  notesUsed: number;
  model: string;
}

export interface AiRewriteResult {
  rewritten: string;
  changes: string[];
  model: string;
}

export interface AssessmentReport {
  id: number;
  assignmentId: number;
  category: string;
  title: string;
  content: string;
  initialOverallRating?: 'red' | 'yellow' | 'green' | null;
  initialAreaRating?: 'red' | 'yellow' | 'green' | null;
  afterPartNotes?: string | null;
  metadata?: {
    initialOverallRating?: 'red' | 'yellow' | 'green' | null;
    initialAreaRating?: 'red' | 'yellow' | 'green' | null;
    afterPartNotes?: string | null;
    aiAssist?: AiAssistMetadata;
    [key: string]: any;
  };
  status: 'draft' | 'submitted' | 'reviewed' | 'approved' | 'returned';
  overallRating?: number | null;
  overallRecommendation?: string | null;
  updatedAt?: string;
  createdAt?: string;
}

export interface AssessmentFinalReport {
  id: number;
  assessmentId: number;
  participantId: number;
  finalRecommendation?: 'red' | 'yellow' | 'green' | null;
  growthRecommendations?: string | null;
  mbcbRecommendations?: string | null;
  finalRecommendationDiscussionMinutes?: number | null;
  callingPhotoImagePath?: string | null;
  discussionPhotoImagePath?: string | null;
  publishedToCandidateAt?: string | null;
  publishedBy?: number | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface FinalReportAreaEntry {
  roleId: number;
  roleName: string;
  assessorName: string;
  /** The assessor's own words, never rewritten by the report writer. */
  reportNotes: string;
  reportId?: number | null;
  reportStatus: AssessmentReport['status'] | null;
  reportUpdatedAt?: string | null;
  afterPartNotes?: string;
  initialOverallRating?: 'red' | 'yellow' | 'green' | null;
  initialAreaRating?: 'red' | 'yellow' | 'green' | null;
  /** The wording that actually goes to the candidate. */
  finalContent?: string;
  isEdited?: boolean;
  /** The assessor has revised their text since the writer last edited this area. */
  isStale?: boolean;
  sectionUpdatedAt?: string | null;
  sourceContentSnapshot?: string;
  sourceReportUpdatedAt?: string | null;
}

export interface FinalReportDelivery {
  id: number;
  channel: 'email' | 'plant';
  status: 'sent' | 'failed' | 'revoked';
  recipientName?: string | null;
  recipientEmail?: string | null;
  note?: string | null;
  error?: string | null;
  sentAt: string;
  sentByUser?: { id: number; firstName: string; lastName: string } | null;
}

export interface FinalReportRecipient {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ReportAssignment {
  id: number | null;
  assessmentId: number;
  participantId: number;
  roleId: number;
  assessorId: number | null;
  assistantId?: number | null;
  status: string;
  teamKey?: 'A' | 'B';
  participant?: AssessmentParticipant;
  role?: {
    id: number;
    name: string;
    displayName: string;
    roleType: 'assessor' | 'support';
    isInterviewArea: boolean;
    interviewAudience?: 'couple' | 'candidate_only' | 'spouse_only';
    sortOrder?: number;
  };
  assessor?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  assistant?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  latestReport?: AssessmentReport | null;
  scorecardOverview?: ScorecardOverview | null;
}

export interface ScorecardOverview {
  average: number | null;
  ratedCount: number;
  totalCount: number;
}

export type FinalReportAiEditModeId = 'clean' | 'soften' | 'tighten' | 'consistency' | 'polish';

export interface FinalReportAiEditMode {
  id: FinalReportAiEditModeId | string;
  label: string;
  description: string;
}

export interface FinalReportAiAssist {
  enabled: boolean;
  modes: FinalReportAiEditMode[];
}

export type FinalReportAiEditScope = 'full' | 'selection';

export interface FinalReportAiEditResult {
  model: string;
  mode: string;
  scope?: FinalReportAiEditScope;
  /** The suggested text (whole text or the edited excerpt); nothing is saved until the writer applies it. */
  edited: string;
  changes: string[];
}

export interface FinalReportResponse {
  participant: AssessmentParticipant;
  finalReport: AssessmentFinalReport | null;
  areaReports: FinalReportAreaEntry[];
  deliveries?: FinalReportDelivery[];
  canEdit: boolean;
  aiAssist?: FinalReportAiAssist;
}

export interface InterviewGuideQuestion {
  id: number;
  prompt: string;
  helpText?: string | null;
  notes: string;
}

export interface InterviewGuideSection {
  section: string | null;
  questions: InterviewGuideQuestion[];
}

export interface InterviewGuideResponse {
  guide: {
    id: number;
    title: string;
    subtitle?: string | null;
    intro?: string | null;
  } | null;
  sections: InterviewGuideSection[];
  generalNotes: string;
  canEdit: boolean;
  /** False when the server has AI turned off or unconfigured; hides the AI toolbar. */
  aiAssistEnabled?: boolean;
}

export type ScorecardSubject = 'candidate' | 'spouse' | 'couple';

export type ScorecardRespondent = 'candidate' | 'spouse' | 'husband' | 'wife';

export type ScorecardRating = 1 | 2 | 3 | 4;

export interface ScorecardStatement {
  id: number;
  statement: string;
  ratings: Partial<Record<ScorecardRespondent, ScorecardRating | null>>;
}

export interface ScorecardNarrative {
  strengths: string;
  growthAreas: string;
  recommendations: string;
}

export interface ScorecardResponse {
  scorecard: {
    guideId: number;
    title: string;
    subject: ScorecardSubject;
  } | null;
  respondents: ScorecardRespondent[];
  statements: ScorecardStatement[];
  summary: ScorecardNarrative;
  canEdit: boolean;
}

export interface ScorecardSavePayload {
  ratings?: Array<{
    statementId: number;
    respondent: ScorecardRespondent;
    rating: ScorecardRating | null;
  }>;
  strengths?: string;
  growthAreas?: string;
  recommendations?: string;
}

export interface PortalCandidateProfile {
  id: number;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  maritalStatus?: string | null;
  wifeFirstName?: string | null;
  wifeLastName?: string | null;
  wifeEmail?: string | null;
  wifePhone?: string | null;
  childrenNamesAges?: string | null;
  targetLocationMississippi?: string | null;
  willBeSouthernBaptist?: string | null;
  ministryLeadershipPositions?: string | null;
  ministryInfluences?: string | null;
  theologicalTraining?: string | null;
  sermonReferenceUrl?: string | null;
  willEmailSermon?: boolean;
  headshotImagePath?: string | null;
  familyPhotoImagePath?: string | null;
  spouseName?: string | null;
  location?: string | null;
  churchName?: string | null;
  supportingChurch?: string | null;
  portalStatus?: string | null;
  migratedToChurchPlanterId?: number | null;
  onboardingStep?: number;
  onboardingCompleted?: boolean;
  profileCompleted?: boolean;
  theologyCompleted?: boolean;
  referencesCompleted?: boolean;
  sendingChurchCompleted?: boolean;
  prepareEnrichRequested?: boolean;
  prepareEnrichStatus?: string | null;
  prepareEnrichNotes?: string | null;
  prepareEnrichData?: any;
  backgroundCheckStatus?: string | null;
  backgroundCheckNotes?: string | null;
  whyChurchPlantingCompleted?: boolean;
  whyChurchPlantingData?: Record<string, any> | null;
  spouseInterviewSubmitted?: boolean;
  spouseInterviewSubmittedAt?: string | null;
  spouseInterviewData?: Record<string, any> | null;
  spouseInterviewStatus?: string | null;
  spouseInterviewStatusUpdatedAt?: string | null;
  spouseInterviewNotes?: string | null;
  ministryTransitionsCompleted?: boolean;
  ministryTransitionsData?: Record<string, any> | null;
  spiritualHealthCompleted?: boolean;
  spiritualHealthCandidateSubmitted?: boolean;
  spiritualHealthSpouseSubmitted?: boolean;
  spiritualHealthNotes?: string | null;
  portalProgressPercentage?: number;
  teamProgressPercentage?: number;
  sendingChurch?: {
    churchName?: string | null;
    primaryContactName?: string | null;
    primaryContactPhone?: string | null;
    noSendingChurch?: boolean;
    noSendingChurchExplanation?: string | null;
  } | null;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  references?: Array<{
    id: number;
    name: string;
    email: string;
    phone?: string | null;
    relationshipType?: string | null;
    relationshipDescription?: string | null;
    yearsKnown?: number | null;
    organization?: string | null;
    position?: string | null;
    contactedByHub?: boolean | null;
    contactedByHubAt?: string | null;
    contactedByHubNotes?: string | null;
  }>;
  theologicalSurvey?: Record<string, any> | null;
  spiritualHealthSurveys?: Array<Record<string, any>>;
}

export interface ScheduleSlot {
  id: number;
  dayIndex: number;
  startTime: string;
  endTime: string;
  label?: string | null;
  slotType: 'session' | 'break' | 'meal' | 'other';
  teamKey?: 'A' | 'B' | null;
  track?: string | null;
  interviewer?: string | null;
  location?: string | null;
  visibility?: 'all' | 'assigned';
  assignments?: Array<{
    id: number;
    participantId: number;
    participant?: AssessmentParticipant;
  }>;
}

export interface MasterScheduleItem {
  id: number;
  dayIndex: number;
  startTime: string;
  endTime: string;
  startTimeLabel?: string;
  endTimeLabel?: string;
  label: string;
  slotType: 'session' | 'break' | 'meal' | 'other' | 'interview';
  interviewPeriodIndex?: number | null;
  location?: string | null;
  visibility: 'all' | 'assigned';
  sortOrder?: number;
  representativeSlotId: number;
}

export interface MasterSchedule {
  assessmentId: number;
  assessmentDate: string;
  days: Array<{
    dayIndex: number;
    date: string | null;
    items: MasterScheduleItem[];
  }>;
}

export const assessmentsService = {
  list: async (): Promise<{ success: boolean; data: Assessment[] }> => {
    const res = await apiClient.get('/assessments');
    return res.data;
  },

  get: async (id: number): Promise<{ success: boolean; data: AssessmentDetail }> => {
    const res = await apiClient.get(`/assessments/${id}`);
    return res.data;
  },

  addPortalCandidate: async (assessmentId: number, portalCandidateId: number) => {
    const res = await apiClient.post(`/assessments/${assessmentId}/participants/portal-candidates`, { portalCandidateId });
    return res.data;
  },

  getSchedule: async (assessmentId: number): Promise<{ success: boolean; data: ScheduleSlot[] }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/schedule`);
    return res.data;
  },

  getMasterSchedule: async (assessmentId: number): Promise<{ success: boolean; data: MasterSchedule }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/master-schedule`);
    return res.data;
  },

  getInterviewMatrix: async (assessmentId: number): Promise<{ success: boolean; data: InterviewMatrixResponse }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/interview-matrix`);
    return res.data;
  },

  listReportAssignments: async (assessmentId: number): Promise<{ success: boolean; data: ReportAssignment[] }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/report-assignments`);
    return res.data;
  },

  getFinalReport: async (assessmentId: number, participantId: number): Promise<{ success: boolean; data: FinalReportResponse }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/final-reports/${participantId}`);
    return res.data;
  },

  saveFinalReport: async (
    assessmentId: number,
    participantId: number,
    payload: {
      finalRecommendation?: 'red' | 'yellow' | 'green' | null;
      growthRecommendations?: string | null;
      mbcbRecommendations?: string | null;
      finalRecommendationDiscussionMinutes?: number | null;
    }
  ): Promise<{ success: boolean; data: AssessmentFinalReport }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/final-reports/${participantId}`, payload);
    return res.data;
  },

  saveFinalReportSection: async (
    assessmentId: number,
    participantId: number,
    roleId: number,
    content: string,
  ) => {
    const res = await apiClient.put(
      `/assessments/${assessmentId}/final-reports/${participantId}/sections/${roleId}`,
      { content },
    );
    return res.data;
  },

  resetFinalReportSection: async (assessmentId: number, participantId: number, roleId: number) => {
    const res = await apiClient.delete(
      `/assessments/${assessmentId}/final-reports/${participantId}/sections/${roleId}`,
    );
    return res.data;
  },

  aiEditFinalReportText: async (
    assessmentId: number,
    participantId: number,
    payload: { text: string; mode: string; scope?: FinalReportAiEditScope },
  ): Promise<{ success: boolean; data: FinalReportAiEditResult }> => {
    const res = await apiClient.post(
      `/assessments/${assessmentId}/final-reports/${participantId}/ai/edit`,
      payload,
    );
    return res.data;
  },

  getFinalReportPdf: async (assessmentId: number, participantId: number): Promise<Blob> => {
    const res = await apiClient.get(
      `/assessments/${assessmentId}/final-reports/${participantId}/pdf`,
      { responseType: 'blob' },
    );
    return res.data;
  },

  getFinalReportRecipients: async (
    assessmentId: number,
    participantId: number,
  ): Promise<{ success: boolean; data: FinalReportRecipient[] }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/final-reports/${participantId}/recipients`);
    return res.data;
  },

  sendFinalReportEmail: async (
    assessmentId: number,
    participantId: number,
    payload: { userIds: number[]; note?: string },
  ): Promise<{ success: boolean; data: FinalReportDelivery[]; sentCount: number; failedCount: number }> => {
    const res = await apiClient.post(
      `/assessments/${assessmentId}/final-reports/${participantId}/send-email`,
      payload,
    );
    return res.data;
  },

  publishFinalReport: async (
    assessmentId: number,
    participantId: number,
  ): Promise<{ success: boolean; data: AssessmentFinalReport }> => {
    const res = await apiClient.post(`/assessments/${assessmentId}/final-reports/${participantId}/publish`);
    return res.data;
  },

  unpublishFinalReport: async (
    assessmentId: number,
    participantId: number,
  ): Promise<{ success: boolean; data: AssessmentFinalReport }> => {
    const res = await apiClient.delete(`/assessments/${assessmentId}/final-reports/${participantId}/publish`);
    return res.data;
  },

  uploadFinalReportCallingPhoto: async (
    assessmentId: number,
    participantId: number,
    file: File,
  ): Promise<{ success: boolean; data: AssessmentFinalReport }> => {
    const formData = new FormData();
    formData.append('callingPhotoImage', file);
    const res = await apiClient.post(
      `/assessments/${assessmentId}/final-reports/${participantId}/upload/calling-photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  uploadFinalReportDiscussionPhoto: async (
    assessmentId: number,
    participantId: number,
    file: File,
  ): Promise<{ success: boolean; data: AssessmentFinalReport }> => {
    const formData = new FormData();
    formData.append('discussionPhotoImage', file);
    const res = await apiClient.post(
      `/assessments/${assessmentId}/final-reports/${participantId}/upload/discussion-photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  getReportAssignment: async (assessmentId: number, assignmentId: number): Promise<{ success: boolean; data: ReportAssignment }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/report-assignments/${assignmentId}`);
    return res.data;
  },

  saveReport: async (
    assessmentId: number,
    assignmentId: number,
    payload: {
      title: string;
      content: string;
      status?: 'draft' | 'submitted' | 'reviewed' | 'approved' | 'returned';
      overallRating?: number | null;
      overallRecommendation?: string | null;
      initialOverallRating?: 'red' | 'yellow' | 'green' | null;
      initialAreaRating?: 'red' | 'yellow' | 'green' | null;
      afterPartNotes?: string | null;
      metadata?: {
        initialOverallRating?: 'red' | 'yellow' | 'green' | null;
        initialAreaRating?: 'red' | 'yellow' | 'green' | null;
        afterPartNotes?: string | null;
        aiAssist?: AiAssistMetadata;
        [key: string]: any;
      };
    },
  ): Promise<{ success: boolean; data: AssessmentReport }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/report-assignments/${assignmentId}/report`, payload);
    return res.data;
  },

  aiDraftReport: async (
    assessmentId: number,
    assignmentId: number,
  ): Promise<{ success: boolean; data: AiDraftResult }> => {
    const res = await apiClient.post(`/assessments/${assessmentId}/report-assignments/${assignmentId}/ai/draft`, {});
    return res.data;
  },

  aiRewriteReportText: async (
    assessmentId: number,
    assignmentId: number,
    text: string,
  ): Promise<{ success: boolean; data: AiRewriteResult }> => {
    const res = await apiClient.post(`/assessments/${assessmentId}/report-assignments/${assignmentId}/ai/rewrite`, { text });
    return res.data;
  },

  getInterviewGuide: async (
    assessmentId: number,
    assignmentId: number,
  ): Promise<{ success: boolean; data: InterviewGuideResponse }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/report-assignments/${assignmentId}/interview-guide`);
    return res.data;
  },

  saveInterviewNotes: async (
    assessmentId: number,
    assignmentId: number,
    payload: {
      notes?: Array<{ questionId: number; notes: string }>;
      generalNotes?: string;
    },
  ): Promise<{ success: boolean; savedAt: string }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/report-assignments/${assignmentId}/interview-notes`, payload);
    return res.data;
  },

  getScorecard: async (
    assessmentId: number,
    assignmentId: number,
  ): Promise<{ success: boolean; data: ScorecardResponse }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/report-assignments/${assignmentId}/scorecard`);
    return res.data;
  },

  saveScorecard: async (
    assessmentId: number,
    assignmentId: number,
    payload: ScorecardSavePayload,
  ): Promise<{ success: boolean; savedAt: string }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/report-assignments/${assignmentId}/scorecard`, payload);
    return res.data;
  },

  getEventDetails: async (assessmentId: number): Promise<{ success: boolean; data: AssessmentEventDetails }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/event-details`);
    return res.data;
  },

  getMyAttendance: async (assessmentId: number): Promise<{ success: boolean; data: AssessmentAttendanceEntry | null }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/attendance/me`);
    return res.data;
  },

  updateMyAttendance: async (assessmentId: number, payload: AttendancePayload): Promise<{ success: boolean; data: AssessmentAttendanceEntry }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/attendance/me`, payload);
    return res.data;
  },

  createAttendance: async (assessmentId: number, payload: AttendancePayload): Promise<{ success: boolean; data: AssessmentAttendanceEntry }> => {
    const res = await apiClient.post(`/assessments/${assessmentId}/attendance`, payload);
    return res.data;
  },

  updateAttendance: async (assessmentId: number, attendanceId: number, payload: AttendancePayload): Promise<{ success: boolean; data: AssessmentAttendanceEntry }> => {
    const res = await apiClient.put(`/assessments/${assessmentId}/attendance/${attendanceId}`, payload);
    return res.data;
  },

  getPortalCandidateProfile: async (
    assessmentId: number,
    candidateId: number
  ): Promise<{ success: boolean; data: PortalCandidateProfile }> => {
    const res = await apiClient.get(`/assessments/${assessmentId}/portal-candidates/${candidateId}/profile`);
    return res.data;
  },
};
